import React from 'react';
import { Box, Typography, Paper } from '@mui/material';

function PrivacyPolicy() {
  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Privacy Policy
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last updated: February 21, 2026
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>1. Information We Collect</Typography>
        <Typography variant="body2" paragraph>
          <strong>Account Information:</strong> When you create an account, we collect your email address, display name, and profile photo (if you sign in with Google).
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Search Data:</strong> We store your vehicle searches, saved searches, and favorites to provide personalized features and improve our service.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Location Data:</strong> If you provide a ZIP code, we use it to find vehicles near you and calculate transport cost estimates. We do not track your precise GPS location.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Usage Data:</strong> We collect anonymous usage analytics to understand how the service is used and to improve performance.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Advertising Data:</strong> Our advertising partners may collect information about your browsing activity on the Service using cookies, pixels, and similar technologies to serve relevant ads. This may include pages visited, searches performed, and interactions with ads or sponsored listings.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>AI Agent Data:</strong> If you use our AI-powered services (such as automated negotiation, vehicle acquisition, or delivery coordination), we collect additional information necessary to act on your behalf. This may include your price limits, negotiation preferences, purchase criteria, contact details, and communication history with dealers and service providers. This data is processed by AI systems and may be shared with relevant third parties (dealers, transport carriers, inspection services) as needed to complete transactions you have authorized.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>2. How We Use Your Information</Typography>
        <Typography variant="body2" paragraph>
          We use the information we collect to:
        </Typography>
        <Box component="ul" sx={{ pl: 2, mb: 2 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Provide vehicle search results and market analysis</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Calculate geographic arbitrage opportunities and transport cost estimates</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Save your preferences, favorites, and search history</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Send notifications about saved search matches (if enabled)</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Display relevant advertisements, including banner ads and sponsored listings</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Generate leads for automotive dealers, sales aggregators, and business partners</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Power AI-assisted features including automated negotiation, vehicle acquisition, delivery coordination, and transaction management</Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>Improve and maintain the service</Typography>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>3. Third-Party Services</Typography>
        <Typography variant="body2" paragraph>
          <strong>Firebase (Google):</strong> We use Firebase for authentication, data storage, and hosting. Your data is processed in accordance with Google's privacy policies.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>MarketCheck:</strong> We use the MarketCheck API to retrieve vehicle listing data. Your search parameters (make, model, year, location) are sent to MarketCheck to fulfill search requests. No personally identifiable information is shared.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Advertising Partners:</strong> We work with third-party advertising networks to display banner ads and sponsored listings on the Service. These partners may use cookies and similar tracking technologies to collect browsing data for ad targeting and measurement purposes. Each advertising partner operates under its own privacy policy.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>Lead Generation Partners:</strong> When you interact with certain listings or features, we may share your search criteria (such as vehicle make, model, year, and general location) with automotive dealers, sales aggregators, and other business partners to connect you with relevant offers. We do not share your personal contact information without your explicit consent.
        </Typography>
        <Typography variant="body2" paragraph>
          <strong>AI Service Providers:</strong> Our AI-powered features use third-party AI models (such as Anthropic's Claude) to process your requests, draft communications, and assist with negotiations. Your transaction-related data (vehicle preferences, price parameters, and authorized communications) may be processed by these AI providers in accordance with their data processing agreements with us. We do not send your personal contact information to AI model providers.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>4. Data Storage and Security</Typography>
        <Typography variant="body2" paragraph>
          Your data is stored in Google Cloud Firestore with security rules that restrict access to authenticated users. We use HTTPS encryption for all data in transit. While we take reasonable measures to protect your data, no method of electronic storage is 100% secure.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>5. Your Rights</Typography>
        <Typography variant="body2" paragraph>
          You may access, update, or delete your account information at any time through your profile settings. You can delete your account and all associated data by contacting us. Anonymous browsing data stored in your browser's local storage can be cleared through your browser settings.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>6. Cookies and Local Storage</Typography>
        <Typography variant="body2" paragraph>
          We use browser local storage and session storage to cache search results, recent searches, and user preferences. We use Firebase authentication tokens stored as cookies. Third-party advertising partners may also set cookies to serve relevant ads and measure ad performance. You can manage cookie preferences through your browser settings, though disabling cookies may affect some features of the Service.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>7. Changes to This Policy</Typography>
        <Typography variant="body2" paragraph>
          We may update this privacy policy from time to time. We will notify registered users of significant changes via email or an in-app notice.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>8. Contact</Typography>
        <Typography variant="body2" paragraph>
          If you have questions about this privacy policy, please contact us at privacy@blueshiftauto.com.
        </Typography>
      </Paper>
    </Box>
  );
}

export default PrivacyPolicy;
